import React from 'react';
import PropTypes from 'prop-types';

import Text from 'relements/build/esm/input/text';
import Dropdown from 'relements/build/esm/input/dropdown';
import Checkbox from 'relements/build/esm/input/checkbox';
import { createAlert } from '@src/actions/BotActions';
import {
  AlertTypes,
  DEFAULT_LANGUAGE,
  FEATURE_FLAG_DATA,
} from '@src/constants/MogamboConstants';
import InfoTooltip from '@src/components/InfoTooltip';
import DisplayText from 'relements/build/esm/ui/text';

import BotStore from '@src/store';
import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';
import { GOOGLE_ANALYTICS, FIELD_TO_GA_LABEL_MAPPING } from '@src/const';
import './Basic.scss';

class Basic extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      supermanSupport: BotStore.getSupermanSupport(),
      errorKeys: [],
      language: {
        code: DEFAULT_LANGUAGE,
        native_name: 'English',
        name: 'English',
      },
      languagesSupported: [],
      enableHinglish: false,
      initialHinglishFlagSet: false,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (
      nextProps.supportedLanguages.length > 0 &&
      prevState.languagesSupported.length === 0
    ) {
      return { languagesSupported: nextProps.supportedLanguages };
    }

    if (
      nextProps?.hybridLanguages?.hinglish &&
      !prevState.initialHinglishFlagSet
    ) {
      return {
        enableHinglish: nextProps?.hybridLanguages?.hinglish,
        initialHinglishFlagSet: true,
      };
    }

    // Return null if the state hasn't changed
    return null;
  }

  handleMultiSelectLangaugeChange = (values) => {
    const languageCodes = values.map((languageData) => {
      return languageData.code;
    });

    // Check if user tries to remove English language
    if (!languageCodes.includes(DEFAULT_LANGUAGE)) {
      createAlert(
        'English language cannot be removed',
        null,
        AlertTypes.WARNING
      );
    } else {
      if (!languageCodes.includes('hi')) {
        this.setState({
          enableHinglish: false,
        });
      }
      this.onUpdate('languagesSupported', languageCodes);
    }
  };

  renderMultiSelectLanguageDropdown = () => {
    const languageValue = [];
    const {
      isSuperman,
      supportedLanguages,
      languages,
      createMode,
    } = this.props;
    const { languagesSupported, language } = this.state;

    if (isSuperman) {
      const supportedLanguageData =
        languagesSupported.length === 0
          ? supportedLanguages
          : languagesSupported;

      // Set values for language select dropdown
      if (supportedLanguageData.length !== 0) {
        languages.forEach((languageItem) => {
          if (supportedLanguageData.includes(languageItem.code)) {
            languageValue.push(languageItem);
          }
        });
      } else {
        // Set default value
        languageValue.push(language);
      }

      /**
       * Check if current mode is create mode and
       * selected langauges does not include the default language;
       * Since we're already including default language when rendering
       * the dropdown, to prevent repetitive occurance of same when state
       * changes and dropdown re-renders. Hence, the latter check.
       */
      if (createMode && !languagesSupported.includes(DEFAULT_LANGUAGE)) {
        languageValue.push(language);
        this.setState({
          languagesSupported: [DEFAULT_LANGUAGE],
        });
      }

      return (
        <Dropdown
          themed
          withSearch
          withMultiple
          options={languages}
          className="bot-properties-input"
          label="Select Languages"
          disabled={BotStore.isProductionEnv()}
          hint="You can add more languages after the bot has been created."
          placeholder="Select items"
          optionKey="name"
          searchKeys={['name', 'native_name', 'code']}
          value={languageValue}
          onChange={this.handleMultiSelectLangaugeChange}
        />
      );
    }
  };

  render() {
    return (
      <div>
        <Text
          themed
          className="bot-properties-input"
          label="* Bot Name (In chatflow)"
          placeholder="Write here..."
          hint="Only lower case characters, numbers, underscore and spaces are allowed"
          value={this.state.chatflowName}
          onChange={(value) => this.onUpdate('chatflowName', value)}
          error={this.state.errorKeys.indexOf('chatflowName') > -1}
        />
        <Dropdown
          themed
          withSearch
          options={this.props.partners}
          className="bot-properties-input"
          label="* Account"
          placeholder="Select an item"
          optionKey="partner_name"
          searchKeys={['partner_name']}
          value={this.state.testPartner}
          onChange={this._handleTestPartnerChange}
          error={this.state.errorKeys.indexOf('testPartner') > -1}
          disabled={!this.props.createMode}
        />
        {!this.state.supermanSupport ? (
          <Dropdown
            themed
            withSearch
            options={this.props.languages}
            className="bot-properties-input"
            label="* Language of Bot"
            hint="You can add more languages after the bot has been created."
            placeholder="Select an item"
            optionKey="name"
            searchKeys={['name', 'native_name', 'code']}
            value={this.state.language}
            onChange={(value) => this.onUpdate('language', value)}
            error={this.state.errorKeys.indexOf('language') > -1}
          />
        ) : (
          <>
            {this.renderMultiSelectLanguageDropdown()}
            {this.state.languagesSupported.includes('hi') &&
              this.renderHinglishCheckbox()}
            {this.renderGlobalConstantInfo()}
          </>
        )}
      </div>
    );
  }

  renderGlobalConstantInfo = () => {
    if (this.state.languagesSupported.length > 1) {
      return (
        <InfoTooltip>
          <DisplayText variant="body.md">
            Please ensure to update the bot responses and bot variables for
            these languages.
          </DisplayText>
        </InfoTooltip>
      );
    }
    return null;
  };

  renderHinglishCheckbox = () => {
    const { enableHinglish } = this.state;
    return (
      <Checkbox
        value={[{ title: enableHinglish && 'Enable Hinglish Responses' }]}
        onChange={() =>
          this.setState({
            enableHinglish: !enableHinglish,
          })
        }
        themed
        mode="stacked"
        options={[{ title: 'Enable Hinglish Responses' }]}
        marginBottom="6px"
        disabled={BotStore.isProductionEnv()}
      />
    );
  };

  onUpdate = (key, value) => {
    this.setState({
      [key]: value,
    });
    if (key === 'languagesSupported' && this.props.onLanguagesChange) {
      this.props.onLanguagesChange(value);
    }

    if (key === 'chatflowName' && this.props.createMode) {
      this.setState({ testBusiness: value });
    }

    if (FIELD_TO_GA_LABEL_MAPPING[key]) {
      sendGoogleAnalyticsEvents(
        GOOGLE_ANALYTICS.CATEGORY.BOT,
        GOOGLE_ANALYTICS.EVENTS.BOT_EDIT,
        FIELD_TO_GA_LABEL_MAPPING[key],
        GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
      );
    }
  };

  _handleTestPartnerChange = (partner) => {
    this.onUpdate('testPartner', partner);
  };

  validate = () => {
    const errorKeys = [];
    /**
     * Since Domain name is not to be rendered on UI,
     * chatFlowName will have same rules as domainName
     */
    if (!this.state.chatflowName) errorKeys.push('chatflowName');
    else if (!this._validateBotName(this.state.chatflowName))
      errorKeys.push('chatflowName');

    if (!this.state.testBusiness) errorKeys.push('testBusiness');
    if (!this.state.testPartner) errorKeys.push('testPartner');
    this.setState({ errorKeys });

    return errorKeys;
  };

  _cleanedBotName = (value) => {
    return value.toLowerCase();
  };

  _validateBotName = (value) => {
    return /^[a-z0-9_ ]+$/.test(this._cleanedBotName(value));
  };
}

Basic.propTypes = {
  createMode: PropTypes.bool,
  businesses: PropTypes.arrayOf(PropTypes.object),
  partners: PropTypes.arrayOf(PropTypes.object),
  languages: PropTypes.arrayOf(PropTypes.object),
  supportedLanguages: PropTypes.arrayOf(PropTypes.string),
  domainId: PropTypes.string,
  isSuperman: PropTypes.bool,
  personalities: PropTypes.arrayOf(PropTypes.object),
  handleEnvUpdate: PropTypes.func,
};

Basic.defaultProps = {
  createMode: false,
  businesses: PropTypes.arrayOf(PropTypes.shape({})),
  partners: PropTypes.arrayOf(PropTypes.shape({})),
  languages: PropTypes.arrayOf(PropTypes.shape({})),
  supportedLanguages: PropTypes.arrayOf(PropTypes.string),
  domainId: PropTypes.string,
  isSuperman: PropTypes.bool,
  personalities: PropTypes.arrayOf(PropTypes.shape({})),
  handleEnvUpdate: () => null,
};

export default Basic;
